import React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import Text from '../Text';
import { useStyles } from '~/hooks';
import { FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    // i18n key, or plain text when `noTranslateLabel` is set.
    label: string;
    noTranslateLabel?: boolean;
    children: React.ReactNode;
    // i18n keys for validation messages; only the visible ones render.
    errors?: { text: string; visible?: boolean }[];
    // Already-composed text shown beside the label, above the control — e.g. an input format.
    hint?: string;
    style?: StyleProp<ViewStyle>;
};

/**
 * Label + control + validation for one form row.
 *
 * Exists so every field on a screen shares the same label type, the same label-to-control gap and
 * the same row rhythm — previously each screen and each field renderer set its own, which is what
 * made the deposit form look assembled from parts.
 */
export default function FormField({ label, noTranslateLabel, children, errors, hint, style }: Props) {
    const styles = useStyles(stylesGenerator);
    return (
        <View style={[styles.row, style]}>
            <View style={styles.labelRow}>
                <Text noTranslate={noTranslateLabel} style={styles.label}>
                    {label}
                </Text>
                {hint ? (
                    <Text noTranslate style={styles.hint}>
                        {hint}
                    </Text>
                ) : null}
            </View>
            {children}
            {errors
                ?.filter((e) => e.visible && e.text)
                .map((e) => (
                    <Text key={e.text} style={styles.error}>
                        {e.text}
                    </Text>
                ))}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        row: {
            marginBottom: scaleSize(16),
        },
        labelRow: {
            flexDirection: 'row',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: scaleSize(6),
        },
        label: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
        },
        hint: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            marginLeft: scaleSize(8),
        },
        error: {
            fontSize: FONT_SIZE_S,
            color: theme.alert,
            marginTop: scaleSize(6),
        },
    });
